import type { OrgRole, PlatformRole } from "./adminTypes";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { ORG_ROLE_LABELS, orgRolesForAddDropdown } from "./adminTypes";

export type AdminInviteUserBlockProps = {
  activeOrgName: string | null;
  effectiveOrgId: string | null;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteRole: OrgRole;
  setInviteRole: (v: OrgRole) => void;
  invitePlatformRole: PlatformRole;
  setInvitePlatformRole: (v: PlatformRole) => void;
  inviteLoading: boolean;
  inviteMsg: string;
  showPlatformRolePicker: boolean;
  canAssignManufacturingOrgRole: boolean;
  onSubmitInvite: () => void;
};

export function AdminInviteUserBlock({
  activeOrgName,
  effectiveOrgId,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  invitePlatformRole,
  setInvitePlatformRole,
  inviteLoading,
  inviteMsg,
  showPlatformRolePicker,
  canAssignManufacturingOrgRole,
  onSubmitInvite,
}: AdminInviteUserBlockProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Send an invite email. The user will receive an invite link and be immediately approved with the selected role for{" "}
        <span className="font-semibold">{activeOrgName ?? effectiveOrgId}</span>. They complete signup from that link,
        then sign in on the app welcome screen with <span className="font-medium">Google</span> (if they use it) or the{" "}
        <span className="font-medium">same email</span> (magic link or password). Invited users bypass the domain check
        and skip the approval queue.
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          type="email"
          {...{ [FIREFLY_TARGET_ATTR]: "admin-invite-email" }}
          className="flex-1 min-w-48 text-sm border border-gray-200 rounded-lg px-3 py-2"
          placeholder="Email address"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          disabled={inviteLoading}
        />
        <select
          {...{ [FIREFLY_TARGET_ATTR]: "admin-invite-role" }}
          className="text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white"
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as OrgRole)}
          disabled={inviteLoading}
          title="Org role"
        >
          {orgRolesForAddDropdown(canAssignManufacturingOrgRole).map((r) => (
            <option key={r} value={r}>
              {ORG_ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        {showPlatformRolePicker && (
          <select
            className="text-sm border border-purple-200 rounded-lg px-2 py-2 bg-white text-purple-800"
            value={invitePlatformRole}
            onChange={(e) => setInvitePlatformRole(e.target.value as PlatformRole)}
            disabled={inviteLoading}
            title="Platform role (optional)"
          >
            <option value="">no platform role</option>
            <option value="platform_dev">platform_dev</option>
            <option value="platform_admin">platform_admin</option>
          </select>
        )}
        <button
          type="button"
          {...{ [FIREFLY_TARGET_ATTR]: "admin-invite-send" }}
          onClick={() => void onSubmitInvite()}
          disabled={inviteLoading || !inviteEmail.trim()}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {inviteLoading ? "Sending…" : "Send Invite"}
        </button>
      </div>
      {inviteMsg && (
        <div
          className={`text-sm px-3 py-2 rounded-lg border ${
            inviteMsg.startsWith("Invite sent") ||
            inviteMsg.startsWith("Existing user") ||
            inviteMsg.startsWith("Pending invite alias")
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : inviteMsg.startsWith("Invite cancelled")
                ? "bg-amber-50 border-amber-200 text-amber-900"
                : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {inviteMsg}
        </div>
      )}
    </div>
  );
}
